import React, { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Check, MessageSquare } from 'lucide-react';
import { NavigationSection, SectionStatus } from './types';
import { Button } from '@/components/ui/button';
import { useSubmissionFormContext } from './SubmissionFormContext';
import { useClarificationsContext } from '@/contexts/ClarificationsContext';
import { useClarificationNavigation } from './ClarificationNavigationContext';
import { useUpdateSubStatus } from '@/hooks/submissions/useUpdateSubStatus';
import { useDebugRole } from '@/contexts/DebugRoleContext';
import {
  getSectionLabel,
  getStatusBadgeClasses,
  isFillingInStatus,
} from './statusUtils';
import { isClarificationsActiveStatus } from '@/utils/clarificationStatus';

interface SidebarNavigationProps {
  sections: NavigationSection[];
  progressPercentage: number;
  lastSaved?: string | null;
  isSaving?: boolean;
  isProgressStale?: boolean;
  saveError?: string | null;
  isSubmitting?: boolean;
}

// `null` = nothing to report (a submitted section with no clarifications);
// it keeps the familiar gray circle.
const getStatusIcon = (status: SectionStatus | null) => {
  switch (status) {
    case 'complete':
      return (
        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
          <Check className="w-3 h-3 text-white" />
        </div>
      );
    case 'in_progress':
      return (
        <div className="w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-white rounded-full" />
        </div>
      );
    case 'optional':
      return (
        <div className="w-5 h-5 bg-white border-2 border-blue-300 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-blue-300 rounded-full" />
        </div>
      );
    case 'not_started':
    default:
      return (
        <div className="w-5 h-5 bg-white border-2 border-gray-400 rounded-full flex items-center justify-center">
          <div className="w-2 h-2 bg-gray-400 rounded-full" />
        </div>
      );
  }
};

const getProgressColor = (percentage: number) => {
  if (percentage === 0) {
    return 'bg-gray-400';
  } else if (percentage < 25) {
    return 'bg-red-500';
  } else if (percentage < 50) {
    return 'bg-orange-500';
  } else if (percentage < 75) {
    return 'bg-yellow-500';
  } else {
    return 'bg-green-500';
  }
};

const formatLastSaved = (lastSaved: string | null | undefined) => {
  if (!lastSaved) return null;

  try {
    const date = new Date(lastSaved);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInMinutes < 1440) {
      // Less than 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    } else {
      // More than 24 hours, show the actual date
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch {
    return 'Unknown';
  }
};

const SidebarNavigation = ({
  sections,
  progressPercentage,
  lastSaved,
  isSaving = false,
  isProgressStale = false,
  saveError = null,
  isSubmitting = false,
}: SidebarNavigationProps) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const { goToFirstInSection, goToSection } = useClarificationNavigation();

  // Get submission data from context
  const { submissionFormData, submissionId, refreshSubmissionForm } =
    useSubmissionFormContext();

  // Get clarification permissions and state from context (backend-authoritative)
  const {
    canAddClarifications,
    canReplyOnly,
    clarificationRoundState,
    totalClarificationCount,
    refetch: refetchClarifications,
    hasMaterialToRequestClarifications,
    hasMaterialToSubmitClarificationResponses,
  } = useClarificationsContext();

  const {
    updateSubStatus,
    isUpdating,
    error: updateError,
  } = useUpdateSubStatus();

  // Debug role for sending header with backend requests
  const { debugRole } = useDebugRole();
  const getDebugHeaders = useCallback((): Record<string, string> => {
    if (debugRole === 'real') return {};
    return { 'X-Debug-Role': debugRole };
  }, [debugRole]);

  // Determine workflow state using backend permission flags as single source of truth.
  // canAddClarifications = admin who can create threads; canReplyOnly = customer in pending state.
  const isPendingClarification = clarificationRoundState === 'requested';
  // Also selects the label vocabulary: form-completion while filling in,
  // clarifications only once submitted (see getSectionLabel)
  const isDraft = isFillingInStatus(submissionFormData?.status);
  const isClarificationsActive = isClarificationsActiveStatus(
    submissionFormData?.status
  );

  const showRequestClarifications =
    canAddClarifications && isClarificationsActive && !isPendingClarification;

  const showSubmitClarifications = canReplyOnly && isClarificationsActive;

  // Show syncing state when progress is stale OR actively saving, but not during
  // form submission — the submit button area has its own "Submitting..." feedback.
  const isSyncing = !isSubmitting && (isSaving || isProgressStale);

  // When clarifications are active, drive the progress bar from their resolved
  // status rather than form-fill completion.
  const resolvedClarificationCount = sections.reduce((sum, section) => {
    const total = section.totalClarificationCount ?? 0;
    const unresolved = section.unresolvedClarificationCount ?? 0;
    return sum + Math.max(0, total - unresolved);
  }, 0);
  const effectiveProgressPercentage =
    totalClarificationCount > 0
      ? Math.min(
          100,
          (resolvedClarificationCount / totalClarificationCount) * 100
        )
      : progressPercentage;

  const handleRequestClarifications = async () => {
    if (!submissionId) return;
    try {
      await updateSubStatus(
        submissionId,
        'pending_clarification',
        'Administrator',
        getDebugHeaders()
      );
      await refreshSubmissionForm();
      await refetchClarifications();
    } catch (err) {
      console.error('Failed to request clarifications:', err);
    }
  };

  const handleSubmitClarifications = async () => {
    if (!submissionId) return;
    try {
      await updateSubStatus(
        submissionId,
        'clarification_received',
        'User',
        getDebugHeaders()
      );
      await refreshSubmissionForm();
      await refetchClarifications();
    } catch (err) {
      console.error('Failed to submit clarifications:', err);
    }
  };

  // Track which section is currently visible
  useEffect(() => {
    const observerOptions = {
      rootMargin: '-20% 0px -80% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id.replace('section-', '');
          setActiveSection(sectionId);
        }
      });
    }, observerOptions);

    // Observe all section elements
    sections.forEach(({ id }) => {
      const element = document.getElementById(`section-${id}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, [sections]);

  // A section with open clarifications takes you to the first one, like
  // landing on the next thing to do; otherwise to the top of the section
  // (expanding it first if it was collapsed).
  const handleSectionClick = (
    sectionId: string,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
    if (goToFirstInSection(sectionId)) return;
    goToSection(sectionId);
  };

  if (sections.length === 0) {
    return (
      <div className="p-6">
        <p className="text-sm text-gray-500">No sections available</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 py-6 flex-shrink-0">
        <div className="status-header flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Status</h3>
          <span className="text-sm text-gray-500 cursor-default">
            {effectiveProgressPercentage.toFixed(0)}%
            {isSyncing && (
              <span className="ml-2 text-xs text-blue-600">(syncing...)</span>
            )}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            role="progressbar"
            aria-valuenow={effectiveProgressPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${totalClarificationCount > 0 ? 'Clarification completion' : 'Form completion'}: ${effectiveProgressPercentage.toFixed(0)}%`}
            className={`${getProgressColor(effectiveProgressPercentage)} h-2 rounded-full transition-all duration-300 ${isSyncing ? 'opacity-50' : 'opacity-100'}`}
            style={{ width: `${effectiveProgressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Navigation Steps */}
      <div className="px-6 pb-4 overflow-y-auto flex-1 min-h-0">
        <div
          className={`space-y-1 transition-opacity ${isSyncing ? 'opacity-50' : 'opacity-100'}`}
        >
          {sections.map(
            (
              {
                id,
                label,
                status,
                hasClarifications,
                unresolvedClarificationCount,
                totalClarificationCount,
              },
              index
            ) => {
              const isLast = index === sections.length - 1;

              const unresolvedCount = unresolvedClarificationCount ?? 0;
              const sectionLabel = getSectionLabel({
                isFillingIn: isDraft,
                status,
                unresolvedCount,
                totalClarificationCount: hasClarifications
                  ? (totalClarificationCount ?? 0)
                  : 0,
              });

              return (
                <div key={id} className="relative">
                  <button
                    type="button"
                    onClick={event => handleSectionClick(id, event)}
                    title={
                      unresolvedCount > 0
                        ? `Go to first open clarification in ${label}`
                        : undefined
                    }
                    className={`section-item w-full text-left hover:bg-gray-50 rounded-lg p-3 transition-colors cursor-pointer ${
                      activeSection === id ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Status Icon */}
                      <div className="relative flex-shrink-0">
                        {getStatusIcon(sectionLabel?.tone ?? null)}

                        {/* Connecting Line */}
                        {!isLast && (
                          <div className="absolute top-5 left-1/2 transform -translate-x-1/2 w-0.5 h-13 border-l-2 border-dotted border-gray-300 z-9999"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm flex items-center gap-2">
                          {label}
                        </div>

                        {/* Status — open clarifications read as a link to
                            the first one; no label when there's nothing to say */}
                        {/* min-h-6 holds the row height when there's no label,
                            so the fixed-height connecting line still meets
                            the next circle */}
                        <div className="mt-1 min-h-6 flex items-center gap-2">
                          {sectionLabel &&
                            (unresolvedCount > 0 ? (
                              <span className="text-xs text-amber-600 font-medium">
                                {sectionLabel.text}{' '}
                                <span aria-hidden="true">→</span>
                              </span>
                            ) : (
                              <span
                                className={`status-badge ${getStatusBadgeClasses(sectionLabel.tone)}`}
                              >
                                {sectionLabel.text}
                              </span>
                            ))}
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* Clarification Workflow Buttons */}
      {showRequestClarifications && (
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900">
                Request Clarifications
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Notify the submitter that clarifications are needed on this
              submission.
            </p>
            <Button
              type="button"
              onClick={handleRequestClarifications}
              disabled={isUpdating || !hasMaterialToRequestClarifications}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white"
            >
              {isUpdating ? 'Updating...' : 'Request Clarifications'}
            </Button>
            {updateError && (
              <p className="text-xs text-red-600">{updateError}</p>
            )}
          </div>
        </div>
      )}

      {showSubmitClarifications && (
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900">
                Submit Responses
              </p>
            </div>
            <p className="text-xs text-gray-500">
              Mark that you have responded to all items and submit for review.
            </p>
            <Button
              type="button"
              onClick={handleSubmitClarifications}
              disabled={
                isUpdating || !hasMaterialToSubmitClarificationResponses
              }
              className="w-full bg-primary-500 hover:bg-primary-600 text-white"
            >
              {isUpdating ? 'Submitting...' : 'Submit Responses'}
            </Button>
            {updateError && (
              <p className="text-xs text-red-600">{updateError}</p>
            )}
          </div>
        </div>
      )}

      {/* Pending Clarification Status Banner (admin sees while awaiting customer response) */}
      {isPendingClarification && !canReplyOnly && isClarificationsActive && (
        <div className="px-6 py-3 border-t border-amber-200 bg-amber-50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-amber-600" />
            <p className="text-xs text-amber-700 font-medium">
              Clarification responses are pending review
            </p>
          </div>
        </div>
      )}

      {/* Autosave Status Footer — only shown while editing a draft */}
      {isDraft && (
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          {saveError ? (
            <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium">
                  Your changes could not be saved
                </p>
                <p className="text-xs mt-0.5">{saveError}</p>
              </div>
            </div>
          ) : (
            <div className="text-center">
              {isSyncing ? (
                <p className="text-sm text-blue-600 font-medium">
                  {isSaving ? 'Saving changes...' : 'Syncing progress...'}
                </p>
              ) : lastSaved ? (
                <p className="text-sm text-gray-600">
                  Last saved:{' '}
                  <span className="font-medium text-gray-700">
                    {formatLastSaved(lastSaved)}
                  </span>
                </p>
              ) : (
                <p className="text-sm text-gray-500">No changes yet</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SidebarNavigation;
